import { getAuditCellHistoryQueryKey, getAuditRecentEditsQueryKey, useAuditRecentEdits } from "@api/generated/audit/audit";
import type { RecentEdit } from "@api/generated/model";
import type { QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { cellText, shortTime } from "./format";

/** How long a typed cell stays coloured. */
export const RECENT_HOURS = 24;
// asked again this often while the page is open, so what someone else types shows without reloading
const REFRESH = 20_000;

/** The cells people typed in these audited tables in the last ``RECENT_HOURS``: who changed each last (the current
 *  user or someone else) and what it held before. Bulk work (an import, a selection, a formula) is not among them. */
export function useRecentEdits(tables: readonly string[], enabled = true): RecentEdit[] | undefined {
  return useAuditRecentEdits(
    { table: [...tables], hours: RECENT_HOURS },
    { query: { enabled: enabled && tables.length > 0, refetchInterval: REFRESH, staleTime: REFRESH / 2 } },
  ).data;
}

/** After a save: the colours and the cells' histories at once, not at the next refresh. */
export function refreshEdits(client: QueryClient) {
  void client.invalidateQueries({ queryKey: getAuditRecentEditsQueryKey() });
  void client.invalidateQueries({ queryKey: getAuditCellHistoryQueryKey() });
}

/** The class of a recently typed cell: ``edited-mine`` or ``edited-other`` (design.css). */
export const editClass = (e: RecentEdit | undefined) => (e ? (e.mine ? "edited-mine" : "edited-other") : undefined);

/** A value of the change log as text, the table's way (an allele result's "none"…). */
export type EditValueText = (value: unknown, absent: boolean) => string;
export const plainValue: EditValueText = (value) => cellText(value);

/** "You changed this at 14:02 (it was 3.5)". */
export function editNote(e: RecentEdit, t: TFunction, text: EditValueText = plainValue): string {
  const values = { user: e.username, time: shortTime(e.ts), before: text(e.before, e.before_absent) };
  return e.mine ? t("edits.byMe", values) : t("edits.byOther", values);
}

/** How many of these edits are the current user's and how many others' (the counts beside the colours). */
export function editCounts(edits: Iterable<RecentEdit | undefined>): { mine: number; other: number } {
  let mine = 0;
  let other = 0;
  for (const e of edits) {
    if (e?.mine) mine++;
    else if (e) other++;
  }
  return { mine, other };
}
